import React, { useEffect, useState } from 'react';
import { 
  CircularProgress, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemButton,
  IconButton,
  Button,
  Box,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { MDBox } from '../../components/MDBox';
import { AISession, getAISessions, getAISession } from '../../services/aiService';

// Match the primary color used in the main chat
const CHAT_PRIMARY_COLOR = '#f77973';

interface ChatHistoryProps {
  currentSessionId: string | null;
  onSelectSession: (session: AISession) => void;
  onNewChat: () => void;
  refreshTrigger?: number;
}

export const ChatHistory: React.FC<ChatHistoryProps> = ({ 
  currentSessionId, 
  onSelectSession, 
  onNewChat,
  refreshTrigger
}) => {
  const [sessions, setSessions] = useState<AISession[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchSessions = async (pageNum: number, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const data = await getAISessions(pageNum, 20);
      
      if (append) {
        setSessions(prev => [...prev, ...data.sessions]);
      } else {
        setSessions(data.sessions);
      }
      
      setTotalPages(data.pagination.totalPages);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to load history", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchSessions(1);
  }, [refreshTrigger]);

  const handleLoadMore = () => {
    if (page < totalPages && !loadingMore) {
      fetchSessions(page + 1, true);
    }
  };

  const handleSessionClick = async (sessionId: string) => {
    try {
      const fullSession = await getAISession(sessionId);
      onSelectSession(fullSession);
    } catch (err) {
      console.error("Failed to fetch session details", err);
    }
  };

  const formatSessions = (rawSessions: AISession[]) => {
    const sessionGroups: { [date: string]: AISession[] } = {};
    rawSessions.forEach(s => {
      const date = new Date(s.createdAt).toISOString().split('T')[0];
      if (!sessionGroups[date]) sessionGroups[date] = [];
      sessionGroups[date].push(s);
    });

    return rawSessions.map(s => {
      if (s.title) return { ...s, displayTitle: s.title };
      
      const date = new Date(s.createdAt).toISOString().split('T')[0];
      const group = sessionGroups[date];
      const index = group.findIndex(g => g.id === s.id);
      const suffix = group.length > 1 ? ` (${group.length - index})` : '';
      
      // prettier date format? e.g. "Today", "Yesterday" or "Oct 10"
      return { 
        ...s, 
        displayTitle: `Chat ${date}${suffix}` 
      };
    });
  };

  const displaySessions = formatSessions(sessions);

  return (
    <MDBox 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: '#fff'
      }}
    >
      <MDBox 
        p={2.5} 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center"
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          History
        </Typography>
        <IconButton size="small" onClick={() => fetchSessions(1)}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </MDBox>
      
      <MDBox px={2} pb={2}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<AddIcon />}
          onClick={onNewChat}
          sx={{
            bgcolor: CHAT_PRIMARY_COLOR,
            color: '#fff',
            textTransform: 'none',
            borderRadius: '20px',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#e6605a',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }
          }}
        >
          New Chat
        </Button>
      </MDBox>

      <Divider sx={{ my: 0, opacity: 0.6 }} />

      <MDBox sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={24} sx={{ color: CHAT_PRIMARY_COLOR }} />
          </Box>
        ) : (
          <List sx={{ pb: 2 }}>
            {displaySessions.map((session) => (
              <ListItem key={session.id} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton 
                  selected={currentSessionId === session.id}
                  onClick={() => handleSessionClick(session.id)}
                  sx={{
                    mx: 1,
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(247, 121, 115, 0.1)', // Light version of primary
                      '&:hover': {
                        bgcolor: 'rgba(247, 121, 115, 0.15)',
                      }
                    },
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Box mr={1.5} display="flex" alignItems="center" color="text.secondary">
                    <ChatBubbleOutlineIcon fontSize="small" />
                  </Box>
                  <ListItemText 
                    primary={session.displayTitle}
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      fontWeight: currentSessionId === session.id ? 600 : 400,
                      color: currentSessionId === session.id ? 'text.primary' : 'text.secondary',
                      noWrap: true
                    }}
                    secondary={new Date(session.createdAt).toLocaleDateString()}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      sx: { display: 'block', mt: 0.5 }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
        
        {page < totalPages && (
          <Box p={2} display="flex" justifyContent="center">
            <Button 
              size="small" 
              onClick={handleLoadMore}
              disabled={loadingMore}
              sx={{ color: CHAT_PRIMARY_COLOR }}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}
      </MDBox>
    </MDBox>
  );
};